import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery,
    Accordion, AccordionSummary, AccordionDetails, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AiFillBulb } from "react-icons/ai";

// ─── Styled Components (matching the Java page pattern) ───────
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(({ theme }) => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 2fr 2fr",
    gap: "12px",
    padding: "12px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    alignItems: "center",
    fontSize: "0.88rem",
    transition: "background 0.2s",
    "&:hover": { background: "rgba(218,165,32,0.05)" },
    "&:last-child": { borderBottom: "none" },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const SectionAvatar = ({ icon }) => (
    <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38 }}>
        {icon}
    </Avatar>
);

const SectionHeading = ({ icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
        <SectionAvatar icon={icon} />
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
    </Box>
);

const TableHeader = ({ columns }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns.length},1fr)`, gap: "12px", padding: "12px 16px", bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod", fontWeight: "bold", fontSize: "0.82rem", letterSpacing: "0.05em", textTransform: "uppercase" }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

// ─── DATA ─────────────────────────────────────────────────────

const primitiveTypes = [
    { type: "int",       size: "4 bytes",  range: "-2,147,483,648 to 2,147,483,647",    example: "int age = 25;" },
    { type: "short",     size: "2 bytes",  range: "-32,768 to 32,767",                  example: "short s = 100;" },
    { type: "long",      size: "8 bytes",  range: "±9.2 × 10¹⁸",                        example: "long pop = 7800000000LL;" },
    { type: "float",     size: "4 bytes",  range: "±3.4 × 10³⁸  (~7 digits)",           example: "float pi = 3.14f;" },
    { type: "double",    size: "8 bytes",  range: "±1.7 × 10³⁰⁸ (~15 digits)",          example: "double e = 2.71828;" },
    { type: "char",      size: "1 byte",   range: "0 to 255 (ASCII)",                    example: "char grade = 'A';" },
    { type: "bool",      size: "1 byte",   range: "true / false",                        example: "bool ok = true;" },
    { type: "void",      size: "–",        range: "No value (functions only)",           example: "void greet() { ... }" },
];

const modifiers = [
    { mod: "unsigned int",     desc: "Only non-negative values",   example: "unsigned int u = 300;" },
    { mod: "const int",        desc: "Value cannot change",        example: "const int MAX = 100;" },
    { mod: "long long",        desc: "64-bit integer",             example: "long long big = 1e18;" },
    { mod: "unsigned char",    desc: "0 to 255",                   example: "unsigned char byte = 255;" },
    { mod: "signed short",     desc: "Explicit signed 16-bit",     example: "signed short n = -99;" },
];

const variableScopes = [
    { scope: "Local",    desc: "Declared inside a function/block. Only visible inside that block. Destroyed on exit.",                                 example: "void f() { int x = 5; }" },
    { scope: "Global",   desc: "Declared outside all functions. Visible everywhere. Persists for program lifetime.",                                   example: "int counter = 0;  // global" },
    { scope: "Static",   desc: "Retains value between function calls. Local scope but permanent storage.",                                             example: "void f() { static int calls = 0; ++calls; }" },
    { scope: "Register", desc: "Hint to compiler to store in CPU register (fast). Modern compilers mostly ignore this hint.",                          example: "register int i = 0;" },
    { scope: "Extern",   desc: "Declares a variable defined in another file. Links across translation units.",                                          example: "extern int sharedCount;" },
];

const typeConversions = [
    { from: "int → double",    type: "Implicit", note: "Safe — no data loss",         example: "int i=5; double d=i;  // d=5.0" },
    { from: "double → int",    type: "Explicit", note: "Truncates decimal part",       example: "double d=3.9; int i=(int)d; // i=3" },
    { from: "int → char",      type: "Explicit", note: "Stores ASCII value",           example: "int n=65; char c=(char)n; // 'A'" },
    { from: "char → int",      type: "Implicit", note: "Gets ASCII numeric value",     example: "char c='A'; int n=c;  // 65" },
    { from: "static_cast",     type: "C++ Cast",  note: "Preferred over C-style cast",  example: "double d=static_cast<double>(5);" },
    { from: "reinterpret_cast",type: "C++ Cast",  note: "Low-level bit reinterpret",    example: "int* p=reinterpret_cast<int*>(ptr);" },
];

const controlFlow = [
    {
        label: "if / else if / else",
        code: `int score = 85;

if (score >= 90) {
    std::cout << "A";
} else if (score >= 80) {
    std::cout << "B";   // ← prints this
} else if (score >= 70) {
    std::cout << "C";
} else {
    std::cout << "F";
}`,
    },
    {
        label: "switch / case",
        code: `int day = 3;

switch (day) {
    case 1:  std::cout << "Monday";    break;
    case 2:  std::cout << "Tuesday";   break;
    case 3:  std::cout << "Wednesday"; break; // ← prints this
    case 4:  std::cout << "Thursday";  break;
    default: std::cout << "Weekend";   break;
}
// Without break, execution falls through to next case!`,
    },
    {
        label: "Ternary operator",
        code: `int a = 10, b = 20;

// condition ? value_if_true : value_if_false
int max = (a > b) ? a : b;   // max = 20

// Nested ternary (use sparingly):
std::string grade = (score>=90) ? "A" : (score>=80) ? "B" : "C";`,
    },
];

const loops = [
    {
        label: "for loop",
        code: `// Classic for loop
for (int i = 0; i < 5; ++i) {
    std::cout << i << " ";   // 0 1 2 3 4
}

// Range-based for (C++11) — most Pythonic C++ loop
std::vector<int> v = {10, 20, 30};
for (int x : v) {
    std::cout << x << " ";   // 10 20 30
}

// Range-for with reference (avoids copies):
for (const auto& x : v) { ... }`,
    },
    {
        label: "while loop",
        code: `int n = 1;

while (n <= 5) {
    std::cout << n << " ";   // 1 2 3 4 5
    ++n;
}

// ── Common pattern: input validation ──
int input;
while (std::cin >> input && input != -1) {
    process(input);
}`,
    },
    {
        label: "do-while loop",
        code: `int n = 0;

// Body executes AT LEAST ONCE before condition check
do {
    std::cout << "Value: " << n << "\\n";
    ++n;
} while (n < 3);
// Prints: Value: 0  |  Value: 1  |  Value: 2

// Typical use: menu that must show at least once
do {
    showMenu();
    choice = getUserInput();
} while (choice != 0);`,
    },
    {
        label: "break / continue / goto",
        code: `// break — exits the innermost loop
for (int i = 0; i < 10; ++i) {
    if (i == 5) break;
    std::cout << i;   // 0 1 2 3 4
}

// continue — skips current iteration
for (int i = 0; i < 6; ++i) {
    if (i % 2 == 0) continue;
    std::cout << i;   // 1 3 5
}

// goto — jumps to label (avoid in modern code)
for (...) {
    for (...) {
        if (found) goto done;  // breaks nested loop
    }
}
done:
    std::cout << "Done";`,
    },
];

const autoKeyword = [
    { feature: "auto type deduction",   example: 'auto x = 42;          // int\nauto d = 3.14;         // double\nauto s = std::string("hi"); // string' },
    { feature: "auto with containers",  example: 'std::vector<int> v={1,2,3};\nauto it = v.begin();   // vector<int>::iterator\nauto& ref = v[0];      // int&' },
    { feature: "auto return type",      example: 'auto add(int a, int b) -> int {\n    return a + b;\n}' },
    { feature: "decltype",              example: 'int x = 5;\ndecltype(x) y = 10;    // y is int\ndecltype(x+0.0) d;     // d is double' },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function CppFundamentals() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loopTab, setLoopTab] = useState(0);
    const [ctrlTab, setCtrlTab] = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                        border: "1px solid rgba(218,165,32,0.25)",
                        letterSpacing: "0.04em",
                    }}
                >
                    C++ FUNDAMENTALS
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.9rem", fontFamily: "monospace" }}>
                    Data Types · Variables · Control Structures
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════
              SECTION 1 — DATA TYPES & VARIABLES
          ════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Data Types &amp; Variables
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            C++ is a <strong>statically-typed</strong>, compiled language. Every variable must have a type
                            known at <em>compile time</em>. Types directly control memory layout and performance.
                        </Typography>

                        {/* ── Primitive Types ── */}
                        <SectionHeading icon={<AiFillBulb size={20} />} title="Primitive Data Types" />

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Type", "Size", "Range / Info", "Example"]} />}
                            {primitiveTypes.map((item, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.95rem", mb: 0.5 }}>{item.type}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", mb: 0.5 }}>{item.size} · {item.range}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{item.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr 2fr", gap: "12px", padding: "11px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.87rem", "&:hover": { bgcolor: "rgba(218,165,32,0.04)" }, "&:last-child": { borderBottom: "none" } }}>
                                        <Box sx={{ fontWeight: 700, color: "goldenrod", fontFamily: "monospace" }}>{item.type}</Box>
                                        <Box sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>{item.size}</Box>
                                        <Box sx={{ fontSize: "0.83rem", color: "rgba(255,255,255,0.75)" }}>{item.range}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.8rem" }}>{item.example}</Box>
                                    </Box>
                                )
                            )}
                        </Box>

                        {/* ── Type Modifiers ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>⚙️</span>} title="Type Modifiers" />
                        <Typography sx={{ opacity: 0.6, fontSize: "0.88rem", mb: 2 }}>
                            Modifiers change the size or sign behaviour of fundamental types.
                        </Typography>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Modifier", "Description", "Example"]} />}
                            {modifiers.map((item, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontWeight: 700, color: "#e879f9", fontSize: "0.88rem", mb: 0.4 }}>{item.mod}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", mb: 0.5 }}>{item.desc}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{item.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <ResponsiveDataRow key={i}>
                                        <Box sx={{ fontWeight: 700, color: "#e879f9", fontFamily: "monospace", fontSize: "0.83rem" }}>{item.mod}</Box>
                                        <Box sx={{ opacity: 0.75 }}>{item.desc}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.8rem" }}>{item.example}</Box>
                                    </ResponsiveDataRow>
                                )
                            )}
                        </Box>

                        {/* ── Variable Scopes ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔭</span>} title="Variable Scope & Storage Classes" />
                        <Typography sx={{ opacity: 0.6, fontSize: "0.88rem", mb: 2 }}>
                            A variable's <em>scope</em> determines where it's visible; its <em>storage class</em> determines its lifetime.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {variableScopes.map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", p: "14px 18px", display: "flex", flexDirection: isMobile ? "column" : "row", gap: 2, alignItems: isMobile ? "flex-start" : "center" }}>
                                    <Chip label={item.scope} size="small" sx={{ bgcolor: "rgba(218,165,32,0.12)", color: "goldenrod", fontWeight: 700, fontSize: "0.75rem", minWidth: 80 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: "0.85rem", opacity: 0.8, mb: 0.5 }}>{item.desc}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{item.example}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* ── auto & decltype ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>✨</span>} title="auto & decltype (C++11+)" />
                        <Typography sx={{ opacity: 0.6, fontSize: "0.88rem", mb: 2 }}>
                            <code style={{ color: "goldenrod" }}>auto</code> lets the compiler deduce the type — avoids
                            verbose type names while keeping full static typing.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {autoKeyword.map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", p: "14px 18px" }}>
                                    <Typography sx={{ fontSize: "0.82rem", color: "#fbbf24", fontWeight: 700, mb: 1 }}>{item.feature}</Typography>
                                    <CodeBlock>{item.example}</CodeBlock>
                                </Box>
                            ))}
                        </Box>

                        {/* ── Type Conversion ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔄</span>} title="Type Conversion & Casting" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Conversion", "Type", "Note", "Example"]} />}
                            {typeConversions.map((item, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                                            <Typography sx={{ fontWeight: 700, color: "#f0883e", fontSize: "0.88rem" }}>{item.from}</Typography>
                                            <Chip label={item.type} size="small" sx={{ fontSize: "0.68rem", height: 18, bgcolor: item.type === "Implicit" ? "rgba(86,211,100,0.12)" : item.type === "Explicit" ? "rgba(255,107,53,0.12)" : "rgba(168,85,247,0.12)", color: item.type === "Implicit" ? "#56d364" : item.type === "Explicit" ? "#ff6b35" : "#a855f7" }} />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", mb: 0.5 }}>{item.note}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{item.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1.5fr 2fr", gap: "12px", padding: "11px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.87rem", "&:hover": { bgcolor: "rgba(218,165,32,0.04)" }, "&:last-child": { borderBottom: "none" } }}>
                                        <Box sx={{ fontWeight: 700, color: "#f0883e", fontFamily: "monospace", fontSize: "0.82rem" }}>{item.from}</Box>
                                        <Chip label={item.type} size="small" sx={{ fontSize: "0.7rem", height: 20, width: "fit-content", bgcolor: item.type === "Implicit" ? "rgba(86,211,100,0.12)" : item.type === "Explicit" ? "rgba(255,107,53,0.12)" : "rgba(168,85,247,0.12)", color: item.type === "Implicit" ? "#56d364" : item.type === "Explicit" ? "#ff6b35" : "#a855f7" }} />
                                        <Box sx={{ opacity: 0.65, fontSize: "0.82rem" }}>{item.note}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{item.example}</Box>
                                    </Box>
                                )
                            )}
                        </Box>

                    </SectionCard>

                    {/* ════════════════════════════════════════════════
              SECTION 2 — CONTROL STRUCTURES
          ════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Control Structures
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            Control structures direct the <strong>flow of execution</strong>. C++ provides
                            conditional branching, loops, and jump statements — mastering them is essential for
                            writing correct, efficient algorithms.
                        </Typography>

                        {/* ── Conditional Statements ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔀</span>} title="Conditional Statements" />

                        <Tabs
                            value={ctrlTab}
                            onChange={(_, v) => setCtrlTab(v)}
                            sx={{ mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)", "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 }, "& .Mui-selected": { color: "goldenrod !important" }, "& .MuiTabs-indicator": { bgcolor: "goldenrod" } }}
                        >
                            {controlFlow.map((c, i) => <Tab key={i} label={c.label} />)}
                        </Tabs>

                        <CodeBlock>{controlFlow[ctrlTab].code}</CodeBlock>

                        {/* ── Truth table for operators ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔢</span>} title="Logical & Comparison Operators" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { group: "Comparison", color: "#58a6ff", items: [
                                        { op: "==",  desc: "Equal to",           ex: "5 == 5  → true" },
                                        { op: "!=",  desc: "Not equal",          ex: "5 != 3  → true" },
                                        { op: ">",   desc: "Greater than",       ex: "5 > 3   → true" },
                                        { op: "<",   desc: "Less than",          ex: "3 < 5   → true" },
                                        { op: ">=",  desc: "Greater or equal",   ex: "5 >= 5  → true" },
                                        { op: "<=",  desc: "Less or equal",      ex: "4 <= 5  → true" },
                                    ]},
                                { group: "Logical", color: "#7ee787", items: [
                                        { op: "&&",  desc: "AND — both true",    ex: "true && false → false" },
                                        { op: "||",  desc: "OR — either true",   ex: "true || false → true" },
                                        { op: "!",   desc: "NOT — invert",       ex: "!true → false" },
                                        { op: "&",   desc: "Bitwise AND",        ex: "6 & 3  → 2  (110 & 011)" },
                                        { op: "|",   desc: "Bitwise OR",         ex: "6 | 3  → 7  (110 | 011)" },
                                        { op: "^",   desc: "Bitwise XOR",        ex: "6 ^ 3  → 5  (110 ^ 011)" },
                                    ]},
                            ].map(group => (
                                <Box key={group.group} sx={{ bgcolor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", overflow: "hidden" }}>
                                    <Box sx={{ bgcolor: `${group.color}14`, borderBottom: "1px solid rgba(255,255,255,0.06)", px: 2, py: 1 }}>
                                        <Typography sx={{ fontWeight: 700, color: group.color, fontSize: "0.82rem" }}>{group.group} Operators</Typography>
                                    </Box>
                                    {group.items.map((item, j) => (
                                        <Box key={j} sx={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr", gap: "8px", px: 2, py: "8px", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: "0.8rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(255,255,255,0.03)" } }}>
                                            <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: group.color }}>{item.op}</Box>
                                            <Box sx={{ opacity: 0.7 }}>{item.desc}</Box>
                                            <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.76rem" }}>{item.ex}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>

                        {/* ── Loops ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔁</span>} title="Loops" />
                        <Typography sx={{ opacity: 0.6, fontSize: "0.88rem", mb: 2 }}>
                            C++ provides three loop constructs. <code style={{ color: "goldenrod" }}>for</code> when count
                            is known, <code style={{ color: "goldenrod" }}>while</code> when condition-driven,
                            <code style={{ color: "goldenrod" }}> do-while</code> when body must run at least once.
                        </Typography>

                        <Tabs
                            value={loopTab}
                            onChange={(_, v) => setLoopTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)", "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 }, "& .Mui-selected": { color: "goldenrod !important" }, "& .MuiTabs-indicator": { bgcolor: "goldenrod" } }}
                        >
                            {loops.map((l, i) => <Tab key={i} label={l.label} />)}
                        </Tabs>

                        <CodeBlock>{loops[loopTab].code}</CodeBlock>

                        {/* ── Loop comparison table ── */}
                        <Box sx={{ mt: 3, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", overflow: "hidden" }}>
                            <Box sx={{ bgcolor: "rgba(218,165,32,0.08)", borderBottom: "1px solid rgba(255,255,255,0.07)", px: 2, py: 1.5 }}>
                                <Typography sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.82rem" }}>Loop Selection Guide</Typography>
                            </Box>
                            {[
                                { loop: "for",        when: "Count / index known upfront",                     cpp: "for (int i=0; i<n; ++i)" },
                                { loop: "range-for",  when: "Iterate all elements of a container",             cpp: "for (auto& x : container)" },
                                { loop: "while",      when: "Loop until external condition becomes false",      cpp: "while (condition)" },
                                { loop: "do-while",   when: "Body must execute at least once",                 cpp: "do { ... } while (cond);" },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "100px 1fr 1fr", gap: "10px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: "0.83rem", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                    <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod" }}>{row.loop}</Box>
                                    <Box sx={{ opacity: 0.75 }}>{row.when}</Box>
                                    <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{row.cpp}</Box>
                                </Box>
                            ))}
                        </Box>

                        {/* ── Exception handling preview ── */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🛡️</span>} title="Exception Handling" />
                        <Typography sx={{ opacity: 0.6, fontSize: "0.88rem", mb: 2 }}>
                            C++ uses <code style={{ color: "goldenrod" }}>try / catch / throw</code> for structured error handling.
                        </Typography>
                        <CodeBlock>{`#include <stdexcept>
#include <iostream>

int divide(int a, int b) {
    if (b == 0)
        throw std::invalid_argument("Division by zero!");
    return a / b;
}

int main() {
    try {
        std::cout << divide(10, 2) << "\\n";  // 5
        std::cout << divide(10, 0) << "\\n";  // throws!
    }
    catch (const std::invalid_argument& e) {
        std::cerr << "Error: " << e.what() << "\\n";
    }
    catch (const std::exception& e) {
        std::cerr << "Unexpected: " << e.what() << "\\n";
    }
    // ── Common exception types ───────────────────
    // std::out_of_range   → vector::at(), string::at()
    // std::overflow_error → arithmetic overflow
    // std::runtime_error  → general runtime issues
    // std::logic_error    → programming errors
    return 0;
}`}</CodeBlock>

                        {/* ── Quick reference cheat row ── */}
                        <Box sx={{ mt: 4, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 2 }}>
                            {[
                                { label: "if shorthand",  code: "(a>b)?a:b",        note: "Ternary" },
                                { label: "range loop",    code: "for(auto& x:v)",   note: "C++11" },
                                { label: "infinite loop", code: "while(true){...}", note: "Use break!" },
                                { label: "null check",    code: "if(ptr!=nullptr)",  note: "Prefer != null" },
                            ].map((tip, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(218,165,32,0.15)", borderRadius: "10px", p: "12px 14px", textAlign: "center" }}>
                                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", mb: 0.5, textTransform: "uppercase", letterSpacing: 1 }}>{tip.label}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", color: "goldenrod", fontSize: "0.78rem", fontWeight: 700, mb: 0.5 }}>{tip.code}</Typography>
                                    <Chip label={tip.note} size="small" sx={{ fontSize: "0.65rem", height: 16, bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.7)" }} />
                                </Box>
                            ))}
                        </Box>

                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
